import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion.jsx';
import {
  Activity,
  Music,
  Drum,
  Radio,
  BarChart3,
  Piano,
  Mic2,
  Loader2,
  RefreshCw,
  Clock,
  Disc,
  Layers,
  Zap,
  Tag,
  Sparkles,
  FileText,
  Copy,
  Check,
  Wand2,
  Type,
} from 'lucide-react';
import api from '../services/api';
import AnalysisProgressModal from './AnalysisProgressModal';
import CreditConfirmModal from './CreditConfirmModal';
import SectionWaveform from './SectionWaveform';

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

export default function TrackAnalytics({ sessionId, existingAnalysis, userMinutes = 0, onCreditsUsed }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [analysis, setAnalysis] = useState(existingAnalysis || null);
  const [initialized, setInitialized] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [liveMinutes, setLiveMinutes] = useState(userMinutes);
  const [copiedPrompt, setCopiedPrompt] = useState(null);

  const copyToClipboard = async (text, promptType) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPrompt(promptType);
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/analysis-status`);
        const data = response.data;
        
        if (data.track_analysis) {
          setAnalysis(data.track_analysis);
        }
        
        if (data.analysis_progress) {
          if (data.analysis_progress.phase !== 'idle' && 
              data.analysis_progress.phase !== 'complete' &&
              data.analysis_progress.phase !== 'error') {
            setIsInProgress(true);
            setIsModalOpen(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch initial analysis status:', err);
      } finally {
        setInitialized(true);
      }
    };
    
    if (!existingAnalysis) {
      fetchInitialStatus();
    } else {
      setInitialized(true);
    }
  }, [sessionId, existingAnalysis]);

  const handleAnalysisClick = async () => {
    try {
      const response = await api.get('/user/profile');
      setLiveMinutes(response.data.minutes || 0);
    } catch (err) {
      console.error('Failed to fetch user minutes:', err);
    }
    setShowConfirmModal(true);
  };

  const startAnalysis = async () => {
    setShowConfirmModal(false);
    setIsModalOpen(true);
    setIsInProgress(true);
    
    try {
      await api.post(`/sessions/${sessionId}/track-analysis`);
      if (onCreditsUsed) onCreditsUsed();
    } catch (err) {
      console.error('Failed to start analysis:', err);
      setIsInProgress(false);
    }
  };

  const handleAnalysisComplete = (newAnalysis) => {
    setAnalysis(newAnalysis);
    setIsInProgress(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  if (!initialized) {
    return (
      <div className="border rounded-lg p-6 bg-muted/30">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="font-medium">Loading Track Analytics...</span>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <>
        <div className="border rounded-lg p-6 bg-muted/30">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Activity className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h4 className="text-lg font-semibold">Track Analytics</h4>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Get detailed audio analysis including BPM, musical key, chord progressions, 
                melody patterns, drum complexity, spectral characteristics, and instrument detection.
              </p>
            </div>
            <Button onClick={handleAnalysisClick} size="lg" className="mt-2">
              <BarChart3 className="h-5 w-5 mr-2" />
              Analyze Track
            </Button>
          </div>
        </div>
        
        <AnalysisProgressModal
          isOpen={isModalOpen}
          sessionId={sessionId}
          onClose={handleModalClose}
          onComplete={handleAnalysisComplete}
        />

        <CreditConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={startAnalysis}
          operationName="Track Analysis"
          operationCost={0.5}
          currentCredits={liveMinutes}
          icon={BarChart3}
        />
      </>
    );
  }

  return (
    <>
      <div className="border rounded-lg bg-muted/30 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b bg-background/50">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <h4 className="font-semibold text-lg">Track Analytics</h4>
          </div>
          <Button onClick={handleAnalysisClick} size="sm" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-analyze
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-background rounded-xl p-4 text-center border shadow-sm">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <Music className="h-5 w-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-primary">{analysis?.bpm?.value || '-'}</div>
              <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">BPM</div>
              {analysis?.bpm?.confidence && (
                <div className="text-xs text-green-500 mt-1">{analysis.bpm.confidence}% confidence</div>
              )}
            </div>

            <div className="bg-background rounded-xl p-4 text-center border shadow-sm">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <Piano className="h-5 w-5 text-primary" />
              </div>
              <div className="text-3xl font-bold text-primary">
                {analysis?.key || '-'}{analysis?.mode === 'minor' ? 'm' : ''}
              </div>
              <div className="flex items-center justify-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs font-mono">
                  {analysis?.camelot || '?'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {analysis?.mode ? analysis.mode.charAt(0).toUpperCase() + analysis.mode.slice(1) : ''}
                </span>
              </div>
              {analysis?.key_confidence && (
                <div className="text-xs text-green-500 mt-1">{analysis.key_confidence}% confidence</div>
              )}
            </div>

            <div className="bg-background rounded-xl p-4 text-center border shadow-sm">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <Radio className="h-5 w-5 text-primary" />
              </div>
              <div className="text-xl font-bold text-primary">
                {analysis?.spectral_analysis?.brightness || '-'}
              </div>
              <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Brightness</div>
              {analysis?.spectral_analysis?.spectral_centroid_hz && (
                <div className="text-xs text-muted-foreground mt-1">
                  {Math.round(analysis.spectral_analysis.spectral_centroid_hz)} Hz
                </div>
              )}
            </div>

            <div className="bg-background rounded-xl p-4 text-center border shadow-sm">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <Drum className="h-5 w-5 text-primary" />
              </div>
              <div className="text-xl font-bold text-primary">
                {analysis?.drum_analysis?.complexity_description || '-'}
              </div>
              <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Drums</div>
              {analysis?.drum_analysis?.complexity_score && (
                <div className="text-xs text-muted-foreground mt-1">
                  Score: {analysis.drum_analysis.complexity_score}
                </div>
              )}
            </div>
          </div>

          {analysis?.genre && (
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-xl p-4 border">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Tag className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-bold text-primary">{analysis.genre.primary}</h3>
                    {analysis.genre.primary_confidence && (
                      <Badge variant="secondary" className="text-xs">
                        {analysis.genre.primary_confidence}% match
                      </Badge>
                    )}
                    {analysis.genre.mood && (
                      <Badge variant="outline" className="text-xs">
                        <Sparkles className="h-3 w-3 mr-1" />
                        {analysis.genre.mood}
                      </Badge>
                    )}
                  </div>
                  
                  {analysis.genre.secondary?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-xs text-muted-foreground">Also matches:</span>
                      {analysis.genre.secondary.map((g, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {g.name} ({g.confidence}%)
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {analysis.genre.characteristics?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {analysis.genre.characteristics.map((c, i) => (
                        <span key={i} className="text-xs bg-muted/60 text-muted-foreground px-2 py-1 rounded-full">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <Accordion type="multiple" defaultValue={['chords', 'instruments']} className="w-full space-y-2">
            <AccordionItem value="chords" className="border rounded-lg px-4">
              <AccordionTrigger className="text-sm py-3">
                <span className="flex items-center gap-2">
                  <Piano className="h-4 w-4 text-primary" />
                  <span className="font-medium">Chord Progression</span>
                  {analysis?.chord_progression?.chord_count && (
                    <Badge variant="secondary" className="ml-2">
                      {analysis.chord_progression.chord_count} chords
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-3">
                  {analysis?.chord_progression?.progression?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analysis.chord_progression.progression.map((chord, i) => (
                        <Badge key={i} variant="outline" className="text-base px-3 py-1 font-mono">
                          {chord}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No chord progression detected</p>
                  )}
                  {analysis?.chord_progression?.most_common && (
                    <div className="flex gap-4 text-sm text-muted-foreground pt-2 border-t">
                      <div>
                        <span className="text-muted-foreground">Most common: </span>
                        <span className="font-semibold text-foreground">{analysis.chord_progression.most_common}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Unique: </span>
                        <span className="font-semibold text-foreground">{analysis.chord_progression.chord_count} chords</span>
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="melody" className="border rounded-lg px-4">
              <AccordionTrigger className="text-sm py-3">
                <span className="flex items-center gap-2">
                  <Mic2 className="h-4 w-4 text-primary" />
                  <span className="font-medium">Melody Analysis</span>
                  {analysis?.melody_curve?.average_note && (
                    <Badge variant="secondary" className="ml-2">
                      Avg: {analysis.melody_curve.average_note}
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold">{analysis?.melody_curve?.average_note || '-'}</div>
                      <div className="text-xs text-muted-foreground">Average Note</div>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold">
                        {analysis?.melody_curve?.pitch_range_hz ? `${Math.round(analysis.melody_curve.pitch_range_hz)}` : '-'}
                      </div>
                      <div className="text-xs text-muted-foreground">Pitch Range (Hz)</div>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold">
                        {analysis?.melody_curve?.average_pitch_hz ? `${Math.round(analysis.melody_curve.average_pitch_hz)}` : '-'}
                      </div>
                      <div className="text-xs text-muted-foreground">Avg Pitch (Hz)</div>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold">
                        {analysis?.melody_curve?.pitch_variability ? Math.round(analysis.melody_curve.pitch_variability) : '-'}
                      </div>
                      <div className="text-xs text-muted-foreground">Variability</div>
                    </div>
                  </div>
                  
                  {analysis?.melody_curve?.melodic_contour?.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground font-medium">Melodic Contour</div>
                      <div className="h-20 flex items-end gap-0.5 bg-muted/30 rounded-lg p-2">
                        {analysis.melody_curve.melodic_contour.slice(0, 40).map((pitch, i) => {
                          const validPitches = analysis.melody_curve.melodic_contour.filter(p => p > 0);
                          const maxPitch = Math.max(...validPitches);
                          const minPitch = Math.min(...validPitches);
                          const range = maxPitch - minPitch || 1;
                          const height = pitch > 0 ? ((pitch - minPitch) / range) * 100 : 0;
                          return (
                            <div
                              key={i}
                              className="flex-1 bg-primary/70 rounded-t transition-all hover:bg-primary"
                              style={{ height: `${Math.max(height, 5)}%` }}
                              title={`${Math.round(pitch)} Hz`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="drums" className="border rounded-lg px-4">
              <AccordionTrigger className="text-sm py-3">
                <span className="flex items-center gap-2">
                  <Drum className="h-4 w-4 text-primary" />
                  <span className="font-medium">Drum Pattern Analysis</span>
                  {analysis?.drum_analysis?.complexity_description && (
                    <Badge variant="secondary" className="ml-2">
                      {analysis.drum_analysis.complexity_description}
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-lg font-bold">
                      {analysis?.drum_analysis?.complexity_score || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">Complexity Score</div>
                    <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ width: `${Math.min(analysis?.drum_analysis?.complexity_score || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-lg font-bold">
                      {analysis?.drum_analysis?.regularity || 0}%
                    </div>
                    <div className="text-xs text-muted-foreground">Regularity</div>
                    <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${analysis?.drum_analysis?.regularity || 0}%` }}
                      />
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-lg font-bold">
                      {analysis?.drum_analysis?.percussion_presence || 0}%
                    </div>
                    <div className="text-xs text-muted-foreground">Percussion Presence</div>
                    <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 transition-all"
                        style={{ width: `${analysis?.drum_analysis?.percussion_presence || 0}%` }}
                      />
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">{analysis?.drum_analysis?.onset_count || 0}</div>
                    <div className="text-xs text-muted-foreground">Total Onsets</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center sm:col-span-2">
                    <div className="text-lg font-bold">
                      {analysis?.drum_analysis?.average_interval_ms || 0} ms
                    </div>
                    <div className="text-xs text-muted-foreground">Avg Beat Interval</div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="spectral" className="border rounded-lg px-4">
              <AccordionTrigger className="text-sm py-3">
                <span className="flex items-center gap-2">
                  <Radio className="h-4 w-4 text-primary" />
                  <span className="font-medium">Spectral Analysis</span>
                  {analysis?.spectral_analysis?.brightness && (
                    <Badge variant="secondary" className="ml-2">
                      {analysis.spectral_analysis.brightness}
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">
                      {analysis?.spectral_analysis?.spectral_centroid_hz 
                        ? Math.round(analysis.spectral_analysis.spectral_centroid_hz).toLocaleString() 
                        : '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Spectral Centroid (Hz)</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">
                      {analysis?.spectral_analysis?.spectral_bandwidth_hz 
                        ? Math.round(analysis.spectral_analysis.spectral_bandwidth_hz).toLocaleString() 
                        : '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Bandwidth (Hz)</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">
                      {analysis?.spectral_analysis?.spectral_rolloff_hz 
                        ? Math.round(analysis.spectral_analysis.spectral_rolloff_hz).toLocaleString() 
                        : '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Rolloff (Hz)</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">
                      {analysis?.spectral_analysis?.zero_crossing_rate?.toFixed(4) || '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Zero Crossing Rate</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">
                      {analysis?.spectral_analysis?.average_loudness?.toFixed(4) || '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg Loudness</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold">
                      {analysis?.spectral_analysis?.dynamic_range?.toFixed(4) || '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">Dynamic Range</div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="instruments" className="border rounded-lg px-4">
              <AccordionTrigger className="text-sm py-3">
                <span className="flex items-center gap-2">
                  <Music className="h-4 w-4 text-primary" />
                  <span className="font-medium">Detected Instruments</span>
                  {analysis?.instruments?.detected?.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {analysis.instruments.detected.length} detected
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-4">
                  {analysis?.instruments?.detected?.length > 0 ? (
                    <div className="space-y-3">
                      {analysis.instruments.detected.map((inst, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-24 text-sm font-medium truncate">{inst.name}</div>
                          <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all"
                              style={{ width: `${inst.confidence}%` }}
                            />
                          </div>
                          <div className="w-14 text-right text-sm font-medium">
                            {Math.round(inst.confidence)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No instruments detected</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Harmonic Content</span>
                        <span className="text-sm font-bold">{analysis?.instruments?.harmonic_content?.toFixed(1) || 0}%</span>
                      </div>
                      <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${analysis?.instruments?.harmonic_content || 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Percussive Content</span>
                        <span className="text-sm font-bold">{analysis?.instruments?.percussive_content?.toFixed(1) || 0}%</span>
                      </div>
                      <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 transition-all"
                          style={{ width: `${analysis?.instruments?.percussive_content || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="sections" className="border rounded-lg px-4">
              <AccordionTrigger className="text-sm py-3">
                <span className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <span className="font-medium">Song Structure</span>
                  {analysis?.sections?.total_sections && (
                    <Badge variant="secondary" className="ml-2">
                      {analysis.sections.total_sections} sections
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-4">
                  {analysis?.sections?.structure_summary && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1 font-medium">Structure Flow</div>
                      <div className="text-sm font-mono break-words">
                        {analysis.sections.structure_summary}
                      </div>
                    </div>
                  )}
                  
                  {analysis?.sections?.timeline?.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-xs text-muted-foreground font-medium mb-2">Section Timeline</div>
                        <SectionWaveform 
                          sections={analysis.sections.timeline}
                          totalDuration={analysis.sections.timeline.reduce((a, s) => a + s.duration, 0)}
                        />
                      </div>
                      
                      <div className="text-xs text-muted-foreground font-medium mt-4 mb-2">Section-by-Section Breakdown</div>
                      <div className="grid gap-3 mt-3">
                        {analysis.sections.timeline.map((section, i) => (
                          <div 
                            key={i} 
                            className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border border-transparent hover:border-primary/20"
                          >
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-2 min-w-[100px]">
                                <div className={`w-3 h-3 rounded-full ${
                                  section.type === 'Intro' ? 'bg-blue-500' :
                                  section.type === 'Verse' ? 'bg-green-500' :
                                  section.type === 'Pre-Chorus' ? 'bg-yellow-500' :
                                  section.type === 'Chorus' ? 'bg-red-500' :
                                  section.type === 'Bridge' ? 'bg-purple-500' :
                                  section.type === 'Outro' ? 'bg-gray-500' : 'bg-slate-400'
                                }`} />
                                <span className="font-semibold text-sm">{section.type}</span>
                              </div>
                              
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>{formatTime(section.start_time)} - {formatTime(section.end_time)}</span>
                              </div>
                              
                              <Badge variant="outline" className="text-xs">
                                {section.bars} bars
                              </Badge>
                              
                              <Badge 
                                variant="secondary" 
                                className={`text-xs ${
                                  section.energy_level === 'High' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                  section.energy_level === 'Medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                }`}
                              >
                                <Zap className="h-3 w-3 mr-1" />
                                {section.energy_level}
                              </Badge>
                            </div>
                            
                            {section.description && (
                              <div className="mt-2 pt-2 border-t border-muted/50">
                                <div className="flex items-start gap-2">
                                  <FileText className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  <p className="text-sm text-muted-foreground leading-relaxed">
                                    {section.description}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {(section.percussive_ratio !== undefined || section.harmonic_ratio !== undefined) && (
                              <div className="mt-2 flex gap-4 text-xs">
                                {section.percussive_ratio !== undefined && (
                                  <div className="flex items-center gap-1.5">
                                    <Drum className="h-3 w-3 text-orange-500" />
                                    <span className="text-muted-foreground">Percussive:</span>
                                    <span className="font-medium">{section.percussive_ratio}%</span>
                                  </div>
                                )}
                                {section.harmonic_ratio !== undefined && (
                                  <div className="flex items-center gap-1.5">
                                    <Music className="h-3 w-3 text-blue-500" />
                                    <span className="text-muted-foreground">Harmonic:</span>
                                    <span className="font-medium">{section.harmonic_ratio}%</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {analysis?.sections?.repeating_patterns?.length > 0 && (
                    <div className="pt-3 border-t">
                      <div className="text-xs text-muted-foreground font-medium mb-2">Repeating Patterns</div>
                      <div className="flex flex-wrap gap-2">
                        {analysis.sections.repeating_patterns.map((pattern, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {pattern.section_type}: {pattern.occurrences}x
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Recreation Prompts - Hidden for now */}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      
      <AnalysisProgressModal
        isOpen={isModalOpen}
        sessionId={sessionId}
        onClose={handleModalClose}
        onComplete={handleAnalysisComplete}
      />

      <CreditConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={startAnalysis}
        operationName="Track Analysis"
        operationCost={0.5}
        currentCredits={liveMinutes}
        icon={BarChart3}
      />
    </>
  );
}
